import { useEffect, useState, useMemo, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { getOrders, createOrder, updateOrder, deleteOrder, getSKUs, getSupplements, getLabelDefaults } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, X, Copy } from "lucide-react";

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [skus, setSKUs] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [labelDefaults, setLabelDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  
  // Form data
  const [formData, setFormData] = useState({
    order_number: "",
    customer_first_name: "",
    customer_last_name: "",
    items: []
  });

  // Create empty item
  const createEmptyItem = useCallback((defaults = {}) => ({
    id: crypto.randomUUID(),
    mode: "sku", // 'sku' or 'custom'
    base_sku_id: "",
    supplement_ids: [],
    product_name: "",
    serving_size_g: defaults.serving_size_g || 50,
    servings_per_pack: defaults.servings_per_pack || null,
    // Custom mode fields
    custom_ingredients_text: "",
    custom_nutrition: {
      energy_kcal: 0,
      protein_g: 0,
      carbs_g: 0,
      sugars_g: 0,
      fibre_g: 0,
      fat_g: 0,
      sat_fat_g: 0,
      trans_fat_g: 0,
      sodium_mg: 0
    }
  }), []);

  const loadData = useCallback(async () => {
    try {
      const [ordersData, skusData, supplementsData, defaultsData] = await Promise.all([
        getOrders(),
        getSKUs(),
        getSupplements(),
        getLabelDefaults().catch(() => ({}))
      ]);
      setOrders(ordersData);
      setSKUs(skusData);
      setSupplements(supplementsData);
      setLabelDefaults(defaultsData);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = useCallback(() => {
    setFormData({
      order_number: "",
      customer_first_name: "",
      customer_last_name: "",
      items: [createEmptyItem(labelDefaults)]
    });
    setEditingId(null);
    setActiveItemIndex(0);
  }, [createEmptyItem, labelDefaults]);

  const handleOpenDialog = (order = null) => {
    if (order) {
      // Edit mode - convert order items to form format
      const items = order.items.map(item => ({
        id: item.id || crypto.randomUUID(),
        mode: item.is_custom ? "custom" : "sku",
        base_sku_id: item.base_sku_id || "",
        supplement_ids: item.supplement_ids || [],
        product_name: item.product_name || "",
        serving_size_g: item.serving_size_g || labelDefaults.serving_size_g || 50,
        servings_per_pack: item.servings_per_pack || null,
        custom_ingredients_text: item.custom_ingredients_text || item.final_ingredients_text || "",
        custom_nutrition: item.custom_nutrition || {
          energy_kcal: 0, protein_g: 0, carbs_g: 0, sugars_g: 0,
          fibre_g: 0, fat_g: 0, sat_fat_g: 0, trans_fat_g: 0, sodium_mg: 0
        }
      }));

      setFormData({
        order_number: order.order_number,
        customer_first_name: order.customer_first_name,
        customer_last_name: order.customer_last_name,
        items: items.length > 0 ? items : [createEmptyItem(labelDefaults)]
      });
      setEditingId(order.id);
    } else {
      resetForm();
    }
    setActiveItemIndex(0);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.order_number.trim()) {
      toast.error("Please enter an order number");
      return;
    }
    if (!formData.customer_last_name.trim()) {
      toast.error("Please enter customer last name");
      return;
    }
    if (formData.items.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    // Validate items
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      if (item.mode === "sku" && !item.base_sku_id) {
        toast.error(`Please select a SKU for item ${i + 1}`);
        return;
      }
      if (item.mode === "custom" && !item.product_name.trim()) {
        toast.error(`Please enter a product name for custom item ${i + 1}`);
        return;
      }
    }

    // Prepare data for backend
    const orderData = {
      order_number: formData.order_number,
      customer_first_name: formData.customer_first_name,
      customer_last_name: formData.customer_last_name,
      items: formData.items.map(item => ({
        product_name: item.product_name,
        base_sku_id: item.mode === "sku" ? item.base_sku_id : null,
        supplement_ids: item.mode === "sku" ? item.supplement_ids : [],
        is_custom: item.mode === "custom",
        custom_ingredients_text: item.mode === "custom" ? item.custom_ingredients_text : null,
        custom_nutrition: item.mode === "custom" ? item.custom_nutrition : null,
        serving_size_g: parseFloat(item.serving_size_g) || 50,
        servings_per_pack: item.servings_per_pack ? parseFloat(item.servings_per_pack) : null
      }))
    };

    try {
      if (editingId) {
        await updateOrder(editingId, orderData);
        toast.success("Order updated successfully");
      } else {
        await createOrder(orderData);
        toast.success("Order created successfully");
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save order");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      try {
        await deleteOrder(id);
        toast.success("Order deleted successfully");
        loadData();
      } catch (error) {
        toast.error("Failed to delete order");
      }
    }
  };

  // Item management
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, createEmptyItem(labelDefaults)]
    }));
    setActiveItemIndex(formData.items.length);
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) {
      toast.error("Order must have at least one item");
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
    if (activeItemIndex >= formData.items.length - 1) {
      setActiveItemIndex(Math.max(0, formData.items.length - 2));
    }
  };

  const duplicateItem = (index) => {
    const itemToCopy = formData.items[index];
    const newItem = {
      ...itemToCopy,
      id: crypto.randomUUID()
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items.slice(0, index + 1), newItem, ...prev.items.slice(index + 1)]
    }));
    setActiveItemIndex(index + 1);
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const updateCustomNutrition = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { 
          ...item, 
          custom_nutrition: { ...item.custom_nutrition, [field]: parseFloat(value) || 0 }
        } : item
      )
    }));
  };

  const toggleSupplement = (itemIndex, supplementId) => {
    const currentSupplements = formData.items[itemIndex].supplement_ids;
    const newSupplements = currentSupplements.includes(supplementId)
      ? currentSupplements.filter(id => id !== supplementId)
      : [...currentSupplements, supplementId];
    updateItem(itemIndex, 'supplement_ids', newSupplements);
  };

  // Get item display name for tabs
  const getItemDisplayName = (item, index) => {
    if (item.mode === "custom") {
      return item.product_name || `Custom Item ${index + 1}`;
    }
    if (item.base_sku_id) {
      const sku = skus.find(s => s.id === item.base_sku_id);
      if (sku) {
        const suppCount = item.supplement_ids.length;
        return suppCount > 0 ? `${sku.name} +${suppCount}` : sku.name;
      }
    }
    return `Item ${index + 1}`;
  };

  const currentItem = formData.items[activeItemIndex];

  return (
    <AdminLayout>
      <div data-testid="order-management-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-[#5D4037] mb-2">Order Management</h1>
            <p className="text-[#795548]">Create nutrition labels by selecting SKUs and supplements</p>
          </div>
          <Button
            data-testid="add-order-button"
            onClick={() => handleOpenDialog()}
            className="bg-[#5D4037] text-white hover:bg-[#4E342E] flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Order
          </Button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5D4037]"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && orders.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-[#D7CCC8]/50">
            <FileText className="w-16 h-16 text-[#D7CCC8] mx-auto mb-4" />
            <p className="text-[#795548] mb-4">No orders found. Create your first order to generate nutrition labels.</p>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-[#5D4037] text-white hover:bg-[#4E342E]"
            >
              Create First Order
            </Button>
          </div>
        )}

        {/* Orders List */}
        {!loading && orders.length > 0 && (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                data-testid={`order-card-${order.order_number}`}
                className="bg-white rounded-xl shadow-sm border border-[#D7CCC8]/50 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-xl font-semibold text-[#5D4037]">{order.order_number}</h3>
                      <span className="text-xs px-3 py-1 bg-[#EFEBE9] text-[#5D4037] rounded-full">
                        {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <p className="text-sm text-[#795548] mb-4">
                      Customer: <span className="font-medium text-[#5D4037]">{order.customer_first_name} {order.customer_last_name}</span>
                    </p>
                    {order.items && order.items.length > 0 && (
                      <div className="space-y-2">
                        {order.items.map((item, idx) => (
                          <div key={item.id || `order-item-${idx}`} className="bg-[#FAF9F6] p-3 rounded-lg text-sm">
                            <p className="font-medium text-[#5D4037]">{idx + 1}. {item.product_name || 'Unnamed Product'}</p>
                            <p className="text-xs text-[#795548] mt-1">
                              {item.final_ingredients_text ? item.final_ingredients_text.substring(0, 80) + (item.final_ingredients_text.length > 80 ? '...' : '') : 'No ingredients'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      data-testid={`edit-order-${order.order_number}`}
                      onClick={() => handleOpenDialog(order)}
                      className="p-2 hover:bg-[#EFEBE9] rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4 text-[#5D4037]" />
                    </button>
                    <button
                      data-testid={`delete-order-${order.order_number}`}
                      onClick={() => handleDelete(order.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-[#D32F2F]" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Order Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" aria-describedby="order-dialog-description">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="text-xl">{editingId ? "Edit Order" : "New Order"}</DialogTitle>
              <p id="order-dialog-description" className="text-sm text-gray-500">
                Select SKUs and supplements to generate nutrition labels
              </p>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto pr-2">
              <div className="space-y-6">
                {/* Order Details */}
                <div className="bg-[#FAF9F6] p-4 rounded-lg">
                  <h3 className="font-semibold text-[#5D4037] mb-3">Order Details</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Order Number *</Label>
                      <Input
                        data-testid="order-number-input"
                        value={formData.order_number}
                        onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                        placeholder="#1024"
                      />
                    </div>
                    <div>
                      <Label>First Name</Label>
                      <Input
                        data-testid="customer-first-name-input"
                        value={formData.customer_first_name}
                        onChange={(e) => setFormData({ ...formData, customer_first_name: e.target.value })}
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <Label>Last Name *</Label>
                      <Input
                        data-testid="customer-last-name-input"
                        value={formData.customer_last_name}
                        onChange={(e) => setFormData({ ...formData, customer_last_name: e.target.value })}
                        placeholder="Smith"
                      />
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-[#5D4037]">Items ({formData.items.length})</h3>
                    <Button
                      data-testid="add-item-button"
                      onClick={addItem}
                      size="sm"
                      variant="outline"
                      className="border-[#5D4037] text-[#5D4037]"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Item
                    </Button>
                  </div>
                  
                  {/* Item tabs */}
                  <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                    {formData.items.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => setActiveItemIndex(index)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                          activeItemIndex === index 
                            ? 'bg-[#5D4037] text-white' 
                            : 'bg-[#EFEBE9] text-[#5D4037] hover:bg-[#D7CCC8]'
                        }`}
                      >
                        {getItemDisplayName(item, index)}
                      </button>
                    ))}
                  </div>

                  {/* Active Item Editor */}
                  {currentItem && (
                    <div className="border border-[#D7CCC8] rounded-lg p-4 bg-white">
                      {/* Item Header with Mode Toggle */}
                      <div className="flex justify-between items-center mb-4 pb-3 border-b">
                        <div className="flex items-center gap-4">
                          <Label className="text-sm font-medium">Item Type:</Label>
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateItem(activeItemIndex, 'mode', 'sku')}
                              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                currentItem.mode === 'sku' 
                                  ? 'bg-[#5D4037] text-white' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              Select SKU
                            </button>
                            <button
                              onClick={() => updateItem(activeItemIndex, 'mode', 'custom')}
                              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                currentItem.mode === 'custom' 
                                  ? 'bg-[#5D4037] text-white' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              Custom Entry
                            </button>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => duplicateItem(activeItemIndex)}
                            className="p-2 hover:bg-[#EFEBE9] rounded-lg transition-colors"
                            title="Duplicate Item"
                          >
                            <Copy className="w-4 h-4 text-[#5D4037]" />
                          </button>
                          {formData.items.length > 1 && (
                            <button
                              onClick={() => removeItem(activeItemIndex)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove Item"
                            >
                              <X className="w-4 h-4 text-[#D32F2F]" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* SKU Mode */}
                      {currentItem.mode === "sku" && (
                        <div className="space-y-6">
                          {/* SKU Selection */}
                          <div>
                            <Label className="text-base font-semibold mb-2 block">Select Base SKU *</Label>
                            <Select
                              value={currentItem.base_sku_id || "none"}
                              onValueChange={(value) => updateItem(activeItemIndex, 'base_sku_id', value === "none" ? "" : value)}
                            >
                              <SelectTrigger data-testid="sku-select" className="w-full">
                                <SelectValue placeholder="Choose a flour type..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">-- Select SKU --</SelectItem>
                                {skus.map((sku) => (
                                  <SelectItem key={sku.id} value={sku.id}>
                                    {sku.sku_code} - {sku.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Supplement Selection */}
                          {supplements.length > 0 && (
                            <div>
                              <Label className="text-base font-semibold mb-2 block">Add Supplements (5% each)</Label>
                              <p className="text-xs text-[#795548] mb-3">Each supplement adds 5% to the blend. Base SKU adjusts automatically.</p>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {supplements.map((supplement) => (
                                  <label
                                    key={supplement.id}
                                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                                      currentItem.supplement_ids.includes(supplement.id)
                                        ? 'bg-[#8A9A5B]/10 border-[#8A9A5B]'
                                        : 'bg-white border-gray-200 hover:border-[#8A9A5B]'
                                    }`}
                                  >
                                    <Checkbox
                                      checked={currentItem.supplement_ids.includes(supplement.id)}
                                      onCheckedChange={() => toggleSupplement(activeItemIndex, supplement.id)}
                                    />
                                    <span className="text-sm">{supplement.name}</span>
                                  </label>
                                ))}
                              </div>
                              {currentItem.supplement_ids.length > 0 && (
                                <p className="text-sm text-[#5D4037] mt-2 font-medium">
                                  Blend: {100 - currentItem.supplement_ids.length * 5}% base + {currentItem.supplement_ids.length * 5}% supplements
                                </p>
                              )}
                            </div>
                          )}

                          {/* Optional Override */}
                          <div>
                            <Label>Product Name (optional override)</Label>
                            <Input
                              value={currentItem.product_name}
                              onChange={(e) => updateItem(activeItemIndex, 'product_name', e.target.value)}
                              placeholder="Leave blank to auto-generate"
                            />
                          </div>
                        </div>
                      )}

                      {/* Custom Mode */}
                      {currentItem.mode === "custom" && (
                        <div className="space-y-6">
                          <div>
                            <Label>Product Name *</Label>
                            <Input
                              value={currentItem.product_name}
                              onChange={(e) => updateItem(activeItemIndex, 'product_name', e.target.value)}
                              placeholder="e.g., Custom Multigrain Blend"
                            />
                          </div>

                          <div>
                            <Label>Ingredients</Label>
                            <Textarea
                              value={currentItem.custom_ingredients_text}
                              onChange={(e) => updateItem(activeItemIndex, 'custom_ingredients_text', e.target.value)}
                              placeholder="List all ingredients..."
                              rows={2}
                            />
                          </div>

                          {/* Nutrition Values */}
                          <div>
                            <Label className="text-base font-semibold mb-3 block">Nutrition Values (per 100g)</Label>
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <Label className="text-xs">Energy (kcal)</Label>
                                <Input
                                  type="number"
                                  value={currentItem.custom_nutrition.energy_kcal}
                                  onChange={(e) => updateCustomNutrition(activeItemIndex, 'energy_kcal', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Protein (g)</Label>
                                <Input
                                  type="number"
                                  value={currentItem.custom_nutrition.protein_g}
                                  onChange={(e) => updateCustomNutrition(activeItemIndex, 'protein_g', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Carbohydrate (g)</Label>
                                <Input
                                  type="number"
                                  value={currentItem.custom_nutrition.carbs_g}
                                  onChange={(e) => updateCustomNutrition(activeItemIndex, 'carbs_g', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Sugars (g)</Label>
                                <Input
                                  type="number"
                                  value={currentItem.custom_nutrition.sugars_g}
                                  onChange={(e) => updateCustomNutrition(activeItemIndex, 'sugars_g', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Dietary Fibre (g)</Label>
                                <Input
                                  type="number"
                                  value={currentItem.custom_nutrition.fibre_g}
                                  onChange={(e) => updateCustomNutrition(activeItemIndex, 'fibre_g', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Total Fat (g)</Label>
                                <Input
                                  type="number"
                                  value={currentItem.custom_nutrition.fat_g}
                                  onChange={(e) => updateCustomNutrition(activeItemIndex, 'fat_g', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Saturated Fat (g)</Label>
                                <Input
                                  type="number"
                                  value={currentItem.custom_nutrition.sat_fat_g}
                                  onChange={(e) => updateCustomNutrition(activeItemIndex, 'sat_fat_g', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Trans Fat (g)</Label>
                                <Input
                                  type="number"
                                  value={currentItem.custom_nutrition.trans_fat_g}
                                  onChange={(e) => updateCustomNutrition(activeItemIndex, 'trans_fat_g', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Sodium (mg)</Label>
                                <Input
                                  type="number"
                                  value={currentItem.custom_nutrition.sodium_mg}
                                  onChange={(e) => updateCustomNutrition(activeItemIndex, 'sodium_mg', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Serving Info - Common to both modes */}
                      <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t">
                        <div>
                          <Label>Serving Size (g)</Label>
                          <Input
                            type="number"
                            value={currentItem.serving_size_g}
                            onChange={(e) => updateItem(activeItemIndex, 'serving_size_g', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Servings per Pack</Label>
                          <Input
                            type="number"
                            value={currentItem.servings_per_pack || ''}
                            onChange={(e) => updateItem(activeItemIndex, 'servings_per_pack', e.target.value || null)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                data-testid="save-order-button"
                onClick={handleSave}
                className="bg-[#5D4037] text-white hover:bg-[#4E342E]"
              >
                {editingId ? "Update Order" : "Create Order"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default OrderManagement;
